<script>
  import { wheelStore } from '$lib/store/wheelStore';
  import { page } from '$app/stores';
  
  let shareUrl = '';
  let showCopyMessage = false;
  let saving = false;
  let saveError = null;
  let saveSuccess = false;
  let wheelTitle = 'My Wheel';
  
  function generateShareUrl() {
    // Make sure we have names
    if ($wheelStore.names.length < 2) {
      saveError = 'At least 2 names are required to generate a share link';
      setTimeout(() => {
        saveError = null;
      }, 3000);
      return;
    }
    
    // Create an object with the wheel data
    const wheelData = {
      names: $wheelStore.names,
      colors: $wheelStore.colors
    };
    
    // Encode the wheel data as a JSON string, then URI encode it
    const encodedData = encodeURIComponent(JSON.stringify(wheelData));
    const baseUrl = $page.url.origin;
    shareUrl = `${baseUrl}/wheel/temp?data=${encodedData}`;
  }
  
  function copyToClipboard() {
    navigator.clipboard.writeText(shareUrl).then(() => {
      showCopyMessage = true;
      setTimeout(() => {
        showCopyMessage = false;
      }, 3000);
    });
  }
  
  async function saveWheel() {
    // Reset states
    saving = true;
    saveError = null;
    saveSuccess = false;
    
    // Make sure we have at least 2 names
    if ($wheelStore.names.length < 2) {
      saveError = 'At least 2 names are required to save a wheel';
      saving = false;
      return;
    }
    
    try {
      // Prepare wheel data
      const wheelData = {
        names: $wheelStore.names,
        colors: $wheelStore.colors,
        title: wheelTitle
      };
      
      // Send to our API endpoint
      const response = await fetch('/api/save', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(wheelData)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to save wheel');
      }
      
      // Save was successful
      saveSuccess = true;
      shareUrl = `${$page.url.origin}${data.url}`;
    } catch (error) {
      saveError = error.message;
    } finally {
      saving = false;
    }
  }
</script>

<div class="share-container">
  <h2>Save & Share Your Wheel</h2>
  
  <div class="save-section">
    <label for="wheel-title">
      Wheel Title:
      <input 
        type="text" 
        id="wheel-title" 
        bind:value={wheelTitle} 
        placeholder="Enter a title for your wheel"
      />
    </label>
    
    <button 
      class="save-button" 
      on:click={saveWheel} 
      disabled={saving || $wheelStore.names.length < 2}
    >
      {saving ? 'Saving...' : 'Save Wheel'}
    </button>
    
    {#if saveError}
      <div class="error-message">{saveError}</div>
    {/if}
    
    {#if saveSuccess}
      <div class="success-message">
        <p>Wheel saved successfully!</p>
        <p>Your wheel is now available at: <a href={shareUrl} target="_blank">{shareUrl}</a></p>
      </div>
    {/if}
  </div>
  
  <div class="divider"></div>
  
  <p>Or create a temporary link to share this wheel without saving:</p>
  
  <button class="share-button" on:click={generateShareUrl} disabled={$wheelStore.names.length < 2}>
    Generate Temporary Link
  </button>
  
  {#if shareUrl && !saveSuccess}
    <div class="url-container">
      <input type="text" readonly value={shareUrl} />
      <button on:click={copyToClipboard}>Copy</button>
    </div>
    
    {#if showCopyMessage}
      <div class="copy-message">Link copied to clipboard!</div>
    {/if}
    
    <div class="social-share">
      <a 
        href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent('Check out this wheel spinner I created!')}`}
        target="_blank"
        rel="noopener noreferrer"
        class="social-button twitter"
      >
        Share on Twitter
      </a>
      
      <a 
        href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
        target="_blank"
        rel="noopener noreferrer"
        class="social-button facebook"
      >
        Share on Facebook
      </a>
    </div>
  {/if}
</div>

<style>
  .share-container {
    margin-top: 2rem;
    background-color: #f8f9fa;
    border-radius: 8px;
    padding: 1.5rem;
    box-shadow: 0 2px 5px rgba(0,0,0,0.1);
  }
  
  h2 {
    margin-top: 0;
    margin-bottom: 1rem;
  }
  
  .save-section {
    margin-bottom: 1.5rem;
  }
  
  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
  }
  
  input[type="text"] {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid #ced4da;
    border-radius: 4px;
    margin-top: 0.25rem;
    font-size: 0.875rem;
  }
  
  .save-button {
    padding: 0.5rem 1rem;
    background-color: #28a745;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    width: 100%;
    margin-top: 0.75rem;
  }
  
  .save-button:disabled {
    background-color: #89c395;
    cursor: not-allowed;
  }
  
  .divider {
    height: 1px;
    background-color: #e9ecef;
    margin: 1.5rem 0;
  }
  
  .share-button {
    padding: 0.5rem 1rem;
    background-color: #007bff;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    width: 100%;
    margin-top: 0.5rem;
  }
  
  .share-button:disabled {
    background-color: #74b4fc;
    cursor: not-allowed;
  }
  
  .url-container {
    display: flex;
    margin-top: 1rem;
  }
  
  .url-container input {
    flex: 1;
    padding: 0.5rem;
    border: 1px solid #ced4da;
    border-radius: 4px 0 0 4px;
    font-size: 0.875rem;
  }
  
  .url-container button {
    padding: 0.5rem 1rem;
    background-color: #6c757d;
    color: white;
    border: none;
    border-radius: 0 4px 4px 0;
    cursor: pointer;
  }
  
  .copy-message {
    margin-top: 0.5rem;
    font-size: 0.875rem;
    color: #28a745;
  }
  
  .error-message {
    margin-top: 0.5rem;
    font-size: 0.875rem;
    color: #dc3545;
    padding: 0.5rem;
    background-color: #f8d7da;
    border-radius: 4px;
  }
  
  .success-message {
    margin-top: 0.5rem;
    font-size: 0.875rem;
    color: #155724;
    padding: 0.5rem;
    background-color: #d4edda;
    border-radius: 4px;
  }
  
  .success-message p {
    margin: 0.25rem 0;
  }
  
  .success-message a {
    color: #007bff;
    word-break: break-all;
  }
  
  .social-share {
    margin-top: 1rem;
    display: flex;
    gap: 0.5rem;
  }
  
  .social-button {
    flex: 1;
    display: inline-block;
    padding: 0.5rem;
    text-align: center;
    border-radius: 4px;
    text-decoration: none;
    color: white;
    font-size: 0.875rem;
  }
  
  .twitter {
    background-color: #1da1f2;
  }
  
  .facebook {
    background-color: #3b5998;
  }
</style>